import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, admin, scopedRules, templates, type PreviewBody } from '../lib/api.js';
import type {
  AdminClientsResult,
  PermissionFlag,
  Perms,
  PreviewResult,
  Rule,
  RuleInput,
  RuleTarget,
} from '../lib/types.js';
import './RuleEditor.css';

const FLAGS: { key: PermissionFlag; label: string; scrittura?: boolean }[] = [
  { key: 'list', label: 'Vedere' },
  { key: 'download', label: 'Scaricare' },
  { key: 'upload', label: 'Caricare', scrittura: true },
  { key: 'delete', label: 'Cancellare', scrittura: true },
  { key: 'createFolder', label: 'Creare cartelle', scrittura: true },
  { key: 'rename', label: 'Rinominare', scrittura: true },
];

const emptyPerms: Perms = {
  list: false,
  download: false,
  upload: false,
  delete: false,
  createFolder: false,
  rename: false,
};

/**
 * Chi subisce la regola. Serve nei testi: "il cliente" è sbagliato su una
 * regola scritta per una singola persona, e la differenza è esattamente quella
 * che l'admin deve avere in mente mentre spunta le caselle.
 */
const SOGGETTO: Record<RuleTarget['scope'], string> = {
  template: 'le organizzazioni con questo template',
  client: "l'organizzazione",
  user: "l'utenza",
};

/**
 * Editor di una regola, uguale per i tre livelli (§6.5).
 *
 * Cambia solo il bersaglio: dove si salva, e su cosa si esegue il dry-run. La
 * prova è **sempre** disponibile, anche per una deroga di una singola persona:
 * è lì che è più facile togliere per sbaglio la radice a qualcuno, perché una
 * regola utente vince su tutto il resto.
 */
export function RuleEditor({
  target,
  rule,
  onClose,
  onSaved,
}: {
  target: RuleTarget;
  rule: Rule | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<RuleInput>(
    rule
      ? {
          pathPattern: rule.pathPattern,
          appliesTo: rule.appliesTo,
          priority: rule.priority,
          note: rule.note,
          permissions: { ...rule.permissions },
        }
      : { pathPattern: '', appliesTo: 'any', priority: 0, note: null, permissions: { ...emptyPerms } },
  );
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [selected, setSelected] = useState<number[]>([]);

  const orgs = useQuery<AdminClientsResult>({
    queryKey: ['admin', 'clients'],
    queryFn: () => admin.clients(),
    enabled: target.scope === 'template',
  });

  // Le organizzazioni su cui la prova ha senso: quelle che usano questo template.
  const candidate =
    target.scope === 'template'
      ? (orgs.data?.clients ?? []).filter((c) => c.isActive && c.templateId === target.templateId)
      : [];

  const previewBody = (): PreviewBody => {
    const base = { rule: form, ruleId: rule?.id ?? null };
    if (target.scope === 'template') {
      return { ...base, scope: 'template', templateId: target.templateId, clientIds: selected };
    }
    if (target.scope === 'client') {
      return { ...base, scope: 'client', clientId: target.clientId };
    }
    return { ...base, scope: 'user', clientId: target.clientId, userId: target.userId };
  };

  const dryRun = useMutation({
    mutationFn: () => templates.preview(previewBody()),
    onSuccess: setPreview,
  });

  const save = useMutation({
    mutationFn: () => {
      if (rule) return templates.updateRule(rule.id, form);
      if (target.scope === 'template') return templates.addRule(target.templateId, form);
      if (target.scope === 'client') return scopedRules.addForClient(target.clientId, form);
      return scopedRules.addForUser(target.clientId, target.userId, form);
    },
    onSuccess: onSaved,
  });

  /**
   * `download` e le scritture implicano `list` (§6.4): il DB lo impone con un
   * CHECK, quindi conviene mantenerlo coerente mentre si spuntano le caselle
   * invece di far salvare e poi mostrare un errore.
   */
  const toggle = (key: PermissionFlag) => {
    setForm((f) => {
      const next = { ...f.permissions, [key]: !f.permissions[key] };
      if (key === 'list' && !next.list) {
        for (const flag of FLAGS) next[flag.key] = false;
      } else if (next[key]) {
        next.list = true;
      }
      return { ...f, permissions: next };
    });
    // Il dry-run vale per i flag di prima: va invalidato.
    setPreview(null);
  };

  const setPattern = (pathPattern: string) => {
    setForm((f) => ({ ...f, pathPattern }));
    setPreview(null);
  };

  const concedeScritture = FLAGS.some((f) => f.scrittura && form.permissions[f.key]);
  const canSave = form.pathPattern.trim() !== '';
  // Per template servono le organizzazioni scelte; per gli altri scope il
  // bersaglio è uno solo ed è già noto.
  const provabile =
    canSave && (target.scope !== 'template' || selected.length > 0);

  return (
    <div className="picker" role="dialog" aria-modal="true">
      <div className="picker__box card ruleed">
        <header className="picker__head">
          <div>
            <h3>{rule ? 'Modifica regola' : 'Nuova regola'}</h3>
            <p className="ruleed__target">{target.label}</p>
          </div>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
            Chiudi
          </button>
        </header>

        <div className="ruleed__body">
          {save.error ? (
            <div className="alert alert--error">
              {save.error instanceof ApiError ? save.error.message : 'Salvataggio non riuscito.'}
            </div>
          ) : null}

          {target.scope === 'user' ? (
            <p className="ruleed__scope-note">
              Una regola personale <strong>vince</strong> su quelle dell'organizzazione e del
              template, e non si somma: sui percorsi che copre decide da sola tutti i permessi.
            </p>
          ) : target.scope === 'client' ? (
            <p className="ruleed__scope-note">
              Una regola dell'organizzazione <strong>vince</strong> sul template e vale per tutte
              le sue utenze, tranne quelle che hanno una regola personale sullo stesso percorso.
            </p>
          ) : null}

          <label className="ruleed__field">
            <span>Percorso</span>
            <input
              className="input ruleed__pattern"
              type="text"
              value={form.pathPattern}
              placeholder="**/1_*"
              onChange={(e) => setPattern(e.target.value)}
              spellCheck={false}
            />
            <small>
              <code>*</code> un pezzo di nome · <code>**</code> qualunque profondità ·{' '}
              <code>**/1_*</code> ogni cartella che inizia per <code>1_</code>, a ogni livello
            </small>
          </label>

          <div className="ruleed__row">
            <label className="ruleed__field">
              <span>Si applica a</span>
              <select
                className="input"
                value={form.appliesTo}
                onChange={(e) => {
                  setForm((f) => ({ ...f, appliesTo: e.target.value as RuleInput['appliesTo'] }));
                  setPreview(null);
                }}
              >
                <option value="any">Cartelle e file</option>
                <option value="folder">Solo cartelle</option>
                <option value="file">Solo file</option>
              </select>
            </label>

            <label className="ruleed__field ruleed__field--narrow">
              <span>Priorità</span>
              <input
                className="input"
                type="number"
                value={form.priority}
                onChange={(e) => {
                  setForm((f) => ({ ...f, priority: Number(e.target.value) }));
                  setPreview(null);
                }}
              />
              <small>A pari specificità vince la più alta.</small>
            </label>
          </div>

          <fieldset className="ruleed__flags">
            <legend>Cosa può fare {SOGGETTO[target.scope]}</legend>
            <div className="ruleed__flag-grid">
              {FLAGS.map((f) => (
                <label key={f.key} className={f.scrittura ? 'is-write' : undefined}>
                  <input
                    type="checkbox"
                    checked={form.permissions[f.key]}
                    onChange={() => toggle(f.key)}
                  />
                  <span>{f.label}</span>
                </label>
              ))}
            </div>
            {concedeScritture ? (
              <p className="ruleed__warn">
                Questa regola concede permessi di scrittura. Esegui la prova prima di salvare.
              </p>
            ) : null}
          </fieldset>

          {/* --- dry-run ------------------------------------------------- */}
          <section className="ruleed__dry">
            <h4>Prova sui documenti reali</h4>
            <p className="ruleed__dry-lead">
              Confronta i permessi prima e dopo, cartella per cartella. Non salva niente.
            </p>

            {/*
              Solo le organizzazioni che usano QUESTO template: su una che ne usa
              un altro la regola non entrerebbe mai nel set, e i numeri sarebbero
              una simulazione senza significato. Per le deroghe di organizzazione
              e di utenza il bersaglio è uno e non si sceglie.
            */}
            {target.scope === 'template' ? (
              candidate.length === 0 ? (
                <p className="ruleed__no-orgs">
                  Nessuna organizzazione attiva usa questo template: non c'è niente su cui
                  provare la regola. Assegnalo a un'organizzazione dalla sezione Organizzazioni.
                </p>
              ) : (
                <>
                  <div className="ruleed__orgs">
                    {candidate.map((c) => (
                      <label key={c.id}>
                        <input
                          type="checkbox"
                          checked={selected.includes(c.id)}
                          onChange={(e) =>
                            setSelected((prev) =>
                              e.target.checked ? [...prev, c.id] : prev.filter((x) => x !== c.id),
                            )
                          }
                        />
                        <span>{c.displayName}</span>
                      </label>
                    ))}
                  </div>
                  {candidate.length > 1 ? (
                    <button
                      type="button"
                      className="ruleed__all"
                      onClick={() =>
                        setSelected(
                          selected.length === candidate.length ? [] : candidate.map((c) => c.id),
                        )
                      }
                    >
                      {selected.length === candidate.length
                        ? 'Deseleziona tutte'
                        : 'Seleziona tutte'}
                    </button>
                  ) : null}
                </>
              )
            ) : (
              <p className="ruleed__dry-target">
                Bersaglio: <strong>{target.label}</strong>
              </p>
            )}

            <button
              type="button"
              className="btn btn--ghost"
              disabled={!provabile || dryRun.isPending}
              onClick={() => dryRun.mutate()}
            >
              {dryRun.isPending ? 'Analisi in corso…' : 'Esegui la prova'}
            </button>

            {dryRun.error ? (
              <div className="alert alert--error">
                {dryRun.error instanceof ApiError ? dryRun.error.message : 'Prova non riuscita.'}
              </div>
            ) : null}

            {preview ? <PreviewReport preview={preview} /> : null}
          </section>
        </div>

        <footer className="picker__foot">
          <span className="ruleed__foot-note">
            {concedeScritture && !preview
              ? 'Concede scritture: conviene provarla prima.'
              : 'Ogni modifica è registrata nel log di audit.'}
          </span>
          <button
            className="btn"
            type="button"
            disabled={!canSave || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Salvataggio…' : 'Salva regola'}
          </button>
        </footer>
      </div>
    </div>
  );
}

function PreviewReport({ preview }: { preview: PreviewResult }) {
  const righe = FLAGS.map((f) => ({
    ...f,
    guadagnati: preview.totali[f.key]?.guadagnati ?? 0,
    persi: preview.totali[f.key]?.persi ?? 0,
  })).filter((r) => r.guadagnati > 0 || r.persi > 0);

  return (
    <div className="ruleed__report">
      {preview.lockout.length > 0 ? (
        <p className="ruleed__report-lockout">
          <strong>Attenzione:</strong> dopo il salvataggio {preview.lockout.join(', ')} non
          vedrebbe più nulla nel portale.
        </p>
      ) : null}

      {righe.length === 0 ? (
        <p className="ruleed__report-none">
          Nessun cambiamento: questa regola non modifica i permessi effettivi sui nodi esaminati.
        </p>
      ) : (
        <>
          {preview.attenzione > 0 ? (
            <p className="ruleed__report-alert">
              Concederebbe permessi di <strong>scrittura</strong> in {preview.attenzione} punti.
            </p>
          ) : null}

          <table className="ruleed__report-table">
            <thead>
              <tr>
                <th>Permesso</th>
                <th className="ruleed__num">Concesso in</th>
                <th className="ruleed__num">Revocato in</th>
              </tr>
            </thead>
            <tbody>
              {righe.map((r) => (
                <tr key={r.key} className={r.scrittura ? 'is-write' : undefined}>
                  <td>{r.label}</td>
                  <td className="ruleed__num">{r.guadagnati > 0 ? `+${r.guadagnati}` : '—'}</td>
                  <td className="ruleed__num">{r.persi > 0 ? `−${r.persi}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {preview.clienti.map((c) => (
            <details key={c.clientId} className="ruleed__client">
              <summary>
                {preview.utente ? `${preview.utente.username} su ${c.displayName}` : c.displayName}{' '}
                — {c.cambiTotali} cambiament{c.cambiTotali === 1 ? 'o' : 'i'} su {c.nodiEsaminati}{' '}
                nodi
              </summary>
              {c.cambi.length === 0 ? (
                <p className="ruleed__report-none">Nessun cambiamento.</p>
              ) : (
                <ul className="ruleed__paths">
                  {c.cambi.map((ch) => (
                    <li key={ch.relPath}>
                      <code>{ch.relPath === '' ? '(radice)' : ch.relPath}</code>
                      {ch.guadagnati.length > 0 ? (
                        <span className="ruleed__gain">+{ch.guadagnati.join(', ')}</span>
                      ) : null}
                      {ch.persi.length > 0 ? (
                        <span className="ruleed__loss">−{ch.persi.join(', ')}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </details>
          ))}
        </>
      )}

      {preview.troncato ? (
        <p className="ruleed__truncated">
          Analisi troncata al limite di {preview.limiti.maxNodi} nodi o {preview.maxDepth} livelli
          di profondità: i conteggi sono parziali.
        </p>
      ) : null}
    </div>
  );
}
